import React, { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { LuClock } from "react-icons/lu";
import { toast, ToastContainer } from "react-toastify";
import { supabase } from "../supabase";
import { FaCalendarDays } from "react-icons/fa6";

function ReserveTripModal({
  showReserve,
  onCloseReserve,
  onSearchReserveRide,
  initialPickup,
  initialDrop,
  initialDropDate,
  initialPickupTime,
  loading,
}) {
  const airportName = "Dibrugarh Airport (DIB)";

  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");

  const [pickupDate, setPickupDate] = useState("");
  const [dropDate, setDropDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [totalDays, setTotalDays] = useState(0);

  const [activeField, setActiveField] = useState(null);

  const [rates, setRates] = useState([]);
  const [filteredRates, setFilteredRates] = useState([]);

  const [pickupSelected, setPickupSelected] = useState(false);
  const [dropSelected, setDropSelected] = useState(false);

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropSuggestions, setDropSuggestions] = useState([]);

  // Fetch Photon suggestions
  const fetchLocations = (query, type) => {
    if (!query || query.length < 3) return;
    if (!window.google) return;

    const service = new window.google.maps.places.AutocompleteService();

    service.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: "IN" }, // still restrict to India
        // types: ["address"], // optional
      },
      (predictions, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          console.warn("Google Places API error:", status);
          return;
        }

        // 🚀 No filtering anymore
        if (type === "pickup") setPickupSuggestions(predictions);
        if (type === "drop") setDropSuggestions(predictions);
      },
    );
  };

  // Debounce typing
  useEffect(() => {
    if (!activeField) return;

    const delay = setTimeout(() => {
      if (activeField === "pickup") fetchLocations(pickup, "pickup");
      if (activeField === "drop") fetchLocations(drop, "drop");
    }, 350);

    return () => clearTimeout(delay);
  }, [pickup, drop, activeField]);
  // ===============================
  // Initialize values
  // ===============================

  useEffect(() => {
    if (!showReserve) return;

    setPickup(initialPickup || "");
    setDrop(initialDrop || "");

    const now = new Date();
    now.setMinutes(now.getMinutes() + 15);

    const today = now.toISOString().split("T")[0];
    const currentTime = now.toTimeString().slice(0, 5);

    // ✅ Pickup Date
    setPickupDate(pickupDate || today);

    // ✅ Drop Date
    setDropDate(initialDropDate || today);

    // ✅ Pickup Time
    setPickupTime(initialPickupTime || currentTime);
  }, [
    showReserve,
    initialPickup,
    initialDrop,
    initialDropDate,
    initialPickupTime,
  ]);

  // ===============================
  // Calculate Total Days
  // ===============================
  useEffect(() => {
    if (pickupDate && dropDate) {
      const start = new Date(pickupDate);
      const end = new Date(dropDate);

      const diff = end - start;

      if (diff >= 0) {
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
        setTotalDays(days);
      } else {
        setTotalDays(0);
      }
    }
  }, [pickupDate, dropDate]);

  // ===============================
  // Filter Drop Suggestions
  // ===============================
  useEffect(() => {
    if (activeField !== "drop") return;

    const delay = setTimeout(() => {
      if (!drop) {
        setFilteredRates([]);
        return;
      }

      const filtered = rates.filter((rate) =>
        rate.place?.toLowerCase().includes(drop.toLowerCase()),
      );

      setFilteredRates(filtered);
    }, 300);

    return () => clearTimeout(delay);
  }, [drop, activeField, rates]);

  // ===============================
  // Search Ride
  // ===============================
  const handleSearchRide = () => {
    if (!pickup || !drop) {
      toast.error("Please enter pickup and drop locations");
      return;
    }

    if (!pickupSelected || !dropSelected) {
      toast.error("Please select pickup and drop from suggestions only");
      return;
    }

    if (!pickupDate || !dropDate || !pickupTime) {
      toast.error("Please select date and time");
      return;
    }

    onSearchReserveRide({
      pickup,
      drop,
      pickupDate,
      dropDate,
      pickupTime,
      totalDays,
    });

    onCloseReserve();
  };

  if (!showReserve) return null;

  return (
    <>
    
      <div className="bottom-modal-overlay" onClick={onCloseReserve}>
        <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header mb-2">
            <h5>Reserve your trip</h5>
            <IoClose className="close-icon" onClick={onCloseReserve} />
          </div>

          {/* PICKUP INPUT */}
        <div className="input-wrapper">
  <input
    type="text"
    placeholder="Enter pickup location"
    className="form-control custom-input"
    value={pickup}
    required
    onFocus={() => setActiveField("pickup")}
    onChange={(e) => {
      setPickup(e.target.value);
      setPickupSelected(false);
    }}
  />

  {pickup && (
    <button
      type="button"
      className="clear-btn"
      onClick={() => {
        setPickup("");
        setPickupSuggestions([]);
        setPickupSelected(false);
      }}
    >
      ✕
    </button>
  )}
</div>
          {/* PICKUP SUGGESTIONS */}
          {activeField === "pickup" && (
            <div className="suggestion">
              {/* Airport Option — hide if already selected in DROP */}
              {drop !== airportName && (
                <div
                  className="suggestion-item highlight-airport"
                  onClick={() => {
                    setPickup(airportName);
                    setPickupSuggestions([]);
                    setActiveField(null);
                    setPickupSelected(true); // ✅ mark selected
                  }}
                >
                  <strong>✈ {airportName}</strong>
                  <div className="small text-muted">Dibrugarh, Assam</div>
                </div>
              )}

              {/* API Suggestions */}
              {pickupSuggestions.map((item, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => {
                    setPickup(item.description);
                    setPickupSuggestions([]);
                    setActiveField(null);
                    setPickupSelected(true); // ✅ mark selected
                  }}
                >
                  {item.description}
                </div>
              ))}
            </div>
          )}

          {/* DROP INPUT */}
          {/* DROP INPUT */}
        <div className="input-wrapper mt-2">
  <input
    type="text"
    placeholder="Enter drop location"
    className="form-control custom-input"
    value={drop}
    required
    onFocus={() => setActiveField("drop")}
    onChange={(e) => {
      setDrop(e.target.value);
      setDropSelected(false);
    }}
  />

  {drop && (
    <button
      type="button"
      className="clear-btn"
      onClick={() => {
        setDrop("");
        setDropSelected(false);
      }}
    >
      ✕
    </button>
  )}
</div>

          {/* DROP SUGGESTIONS */}
          {activeField === "drop" && (
            <div className="suggestion">
              {/* Airport Option — hide if already selected in PICKUP */}
              {pickup !== airportName && (
                <div
                  className="suggestion-item highlight-airport"
                  onClick={() => {
                    setDrop(airportName);
                    setDropSuggestions([]);
                    setActiveField(null);
                    setDropSelected(true); // ✅ mark selected
                  }}
                >
                  <strong>✈ {airportName}</strong>
                  <div className="small text-muted">Dibrugarh, Assam</div>
                </div>
              )}

              {/* API Suggestions */}
              {dropSuggestions.map((item, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => {
                    setDrop(item.description);
                    setDropSuggestions([]);
                    setActiveField(null);
                    setDropSelected(true); // ✅ mark selected
                  }}
                >
                  {item.description}
                </div>
              ))}
            </div>
          )}

          {/* Date & Time Section (Always Visible) */}
          <div className="datetime-box">
            <div className="d-flex gap-2">
              <div className="w-75">
                <label className="form-label mb-0">Pickup Date</label>
                <input
                  type="date"
                  required
                  className="form-control mt-0"
                  value={pickupDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setPickupDate(e.target.value)}
                />
              </div>
              <div className="w-25">
                <label className="form-label mb-0">Pickup Time</label>
                <input
                  type="time"
                  required
                  className="form-control mt-0"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                />
              </div>
            </div>

            <label className="form-label mb-0 mt-2">Return Date</label>
            <input
              type="date"
              className="form-control mt-0"
              value={dropDate}
              required
              min={pickupDate}
              onChange={(e) => setDropDate(e.target.value)}
            />

            {totalDays > 0 && (
              <div className="mt-2">
                <span className="p-2 meta-badge">
                <FaCalendarDays className="icon me-1" />
                No. of Days: {totalDays}
              </span>
              </div>
            )}
          </div>

          {/* Search Button */}
          <button
            className="btn btn-dark py-3 w-100 mt-3"
            onClick={handleSearchRide}
            disabled={loading}
          >
            {loading ? "Updating..." : "Search My Ride"}
          </button>
        </div>
      </div>
    </>
  );
}

export default ReserveTripModal;
